/**
 * 📄 Document Service v2 — Drive-Only
 * 
 * Dokument-Dateien UND Metadaten leben komplett in Google Drive.
 * Metadaten = Dateiname + appProperties. KEIN Firestore für Dokumente!
 * 
 * Kategorien bleiben in Firestore (nur wenige Reads, selten geändert).
 * 
 * Dateiname-Format: {Kategorie}_{Lieferant}_{YYYY-MM-DD}_{NNN}.{ext}
 */

import { api, apiFetch } from './apiService';
import {
  listDriveDocuments,
  listDriveDocumentsPaged,
  uploadDocumentToDrive,
  updateDriveDocument,
  deleteFromDrive,
  downloadDriveFile,
  getDriveFileUrlSync,
  getDriveFileUrl,
  isDriveAvailable,
} from './googleDriveService';

// ============================================================
// 📂 DEFAULT-KATEGORIEN
// ============================================================

export const DEFAULT_CATEGORIES = [
  { id: 'rechnung', name: 'Rechnung', icon: '🧾' },
  { id: 'lieferschein', name: 'Lieferschein', icon: '📦' },
  { id: 'warenschein', name: 'Warenschein', icon: '📋' },
  { id: 'vertrag', name: 'Vertrag', icon: '📑' },
  { id: 'sonstiges', name: 'Sonstiges', icon: '📄' },
];

// ============================================================
// � KATEGORIEN (Server-API)
// ============================================================

export async function loadCategories() {
  const result = await api.get('/documents/categories');
  let cats = result.categories || [];
  cats.sort((a, b) => (a.order || 0) - (b.order || 0));
  return cats;
}

export async function createCategory(categoryData) {
  return api.post('/documents/categories', categoryData);
}

export async function updateCategory(categoryId, updates) {
  return api.put(`/documents/categories/${categoryId}`, updates);
}

export async function deleteCategory(categoryId) {
  return api.delete(`/documents/categories/${categoryId}`);
}

/**
 * Generiert einen Auto-Dateinamen.
 * Format: {Kategorie}_{Lieferant}_{Datum}_{NNN}.{ext}
 */
export function generateDocumentName(categoryName, supplier, date, extension, existingNames = []) {
  const sanitize = (str) => str
    .replace(/[^a-zA-Z0-9äöüÄÖÜß\s-]/g, '')
    .replace(/\s+/g, '-')
    .trim();

  const catPart = sanitize(categoryName || 'Dokument');
  const supplierPart = supplier ? `_${sanitize(supplier)}` : '';
  const datePart = date || new Date().toISOString().split('T')[0];
  const ext = extension || 'jpg';

  const basePattern = `${catPart}${supplierPart}_${datePart}`;
  let nummer = 1;

  existingNames.forEach(name => {
    const match = name.match(new RegExp(`^${basePattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}_(\\d+)\\.`));
    if (match) {
      const n = parseInt(match[1], 10);
      if (n >= nummer) nummer = n + 1;
    }
  });

  return `${basePattern}_${String(nummer).padStart(3, '0')}.${ext}`;
}

/**
 * Komprimiert ein Bild. Nutzt createImageBitmap (kein base64-Umweg via
 * FileReader.readAsDataURL — spart bei 12-MP-Fotos mehrere MB RAM und
 * Hauptthread-Zeit).
 */
export async function compressImage(file, maxWidth = 1200, quality = 0.8) {
  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch (e) {
    // Fallback für Formate, die createImageBitmap nicht decoded (selten)
    return compressImageViaFileReader(file, maxWidth, quality);
  }
  try {
    const scale = bitmap.width > maxWidth ? maxWidth / bitmap.width : 1;
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0, w, h);
    return await new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => blob ? resolve(blob) : reject(new Error('Komprimierung fehlgeschlagen')),
        'image/jpeg',
        quality
      );
    });
  } finally {
    if (bitmap && typeof bitmap.close === 'function') bitmap.close();
  }
}

/**
 * Decoded das Bild einmal und liefert sowohl ein Full-Size- als auch ein
 * Thumbnail-Blob — vermeidet zweimaliges createImageBitmap im UploadModal.
 */
export async function compressImageWithThumb(file, fullMax, thumbMax, quality = 0.85, thumbQuality = 0.7) {
  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch (e) {
    const full = await compressImageViaFileReader(file, fullMax, quality);
    const thumb = await compressImageViaFileReader(full, thumbMax, thumbQuality);
    return { full, thumb };
  }
  try {
    const draw = (maxW, q) => new Promise((resolve, reject) => {
      const scale = bitmap.width > maxW ? maxW / bitmap.width : 1;
      const w = Math.max(1, Math.round(bitmap.width * scale));
      const h = Math.max(1, Math.round(bitmap.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
      canvas.toBlob(
        (blob) => blob ? resolve(blob) : reject(new Error('Komprimierung fehlgeschlagen')),
        'image/jpeg',
        q
      );
    });
    const full = await draw(fullMax, quality);
    const thumb = await draw(thumbMax, thumbQuality);
    return { full, thumb };
  } finally {
    if (bitmap && typeof bitmap.close === 'function') bitmap.close();
  }
}

/**
 * Skaliert ein Kamerafoto EINMAL auf eine maximale Kantenlänge herunter,
 * BEVOR es durch Crop-/Preview-/Merge-Pipeline läuft. Ein rohes 12-MP-Foto
 * wird sonst mehrfach in Vollauflösung dekodiert (Preview-<img>,
 * createImageBitmap, Crop-Canvas) — je ~40–50 MB RGBA gleichzeitig im RAM,
 * was den WebView auf schwachen Geräten beim Erfassen OOM-abstürzen lässt.
 * Nicht-Bilder und bereits kleine Bilder werden unverändert zurückgegeben.
 */
export async function downscaleImageFile(file, maxEdge = 2200, quality = 0.88) {
  if (!file?.type?.startsWith('image/')) return file;
  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch (e) {
    return file; // exotisches Format — lieber Original behalten
  }
  try {
    const longest = Math.max(bitmap.width, bitmap.height);
    if (longest <= maxEdge) return file; // schon klein genug
    const scale = maxEdge / longest;
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', quality)
    );
    if (!blob) return file;
    const baseName = (file.name || 'foto').replace(/\.\w+$/, '');
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
  } finally {
    if (bitmap && typeof bitmap.close === 'function') bitmap.close();
  }
}

function compressImageViaFileReader(file, maxWidth, quality) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => blob ? resolve(blob) : reject(new Error('Komprimierung fehlgeschlagen')),
          'image/jpeg',
          quality
        );
      };
      img.onerror = () => reject(new Error('Bild konnte nicht geladen werden'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden'));
    reader.readAsDataURL(file);
  });
}

/**
 * Wandelt ein Bild (File/Blob) in eine data:-URL um und verkleinert es dabei
 * auf max. `maxSize` px Kantenlänge. Entlastet das Gerät und den Upload.
 * Strings werden unverändert zurückgegeben.
 */
export function imageToDataUrl(source, maxSize = 1600, quality = 0.8) {
  return new Promise((resolve, reject) => {
    if (typeof source === 'string') { resolve(source); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const longest = Math.max(img.width, img.height);
        const scale = Math.min(1, maxSize / longest);
        if (scale >= 1) { resolve(reader.result); return; }
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        try {
          resolve(canvas.toDataURL('image/jpeg', quality));
        } catch (e) {
          resolve(reader.result); // Fallback: Original
        }
      };
      img.onerror = () => resolve(reader.result); // Fallback: Original-DataURL
      img.src = reader.result;
    };
    reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden'));
    reader.readAsDataURL(source);
  });
}

/**
 * Server-seitige Texterkennung (OCR).
 *
 * Ersetzt tesseract.js auf dem Gerät — das WASM-OCR im WebView hat auf
 * schwächeren Handys regelmäßig zu Abstürzen geführt (vor allem beim
 * Hinzufügen weiterer Foto-Seiten). Der Server (POST /api/ocr) macht
 * Rotation + OCR und liefert ggf. zusätzlich strukturierte Felder.
 *
 * @param {File|Blob|string} imageSource - Bild oder data:-URL
 * @returns {Promise<{text:string, confidence:number, structured:object|null, source:string}>}
 */
export async function ocrImage(imageSource) {
  const dataUrl = await imageToDataUrl(imageSource, 1600, 0.8);
  const result = await apiFetch('/ocr', {
    method: 'POST',
    body: JSON.stringify({ image: dataUrl, lang: 'deu+eng' }),
    timeout: 60000,
  });
  return {
    text: (result?.text || '').trim(),
    confidence: result?.confidence || 0,
    structured: result?.structured || null,
    source: result?.source || 'unknown',
  };
}

function getExtension(file) {
  if (file.name) {
    const parts = file.name.split('.');
    if (parts.length > 1) return parts.pop().toLowerCase();
  }
  const mimeMap = {
    'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp',
    'image/heic': 'heic', 'application/pdf': 'pdf',
  };
  return mimeMap[file.type] || 'bin';
}

// ============================================================
//  DOKUMENTE — ALLE OPERATIONEN DIREKT AUF GOOGLE DRIVE
// ============================================================

/**
 * Lädt alle Dokumente aus Google Drive.
 * Keine Firestore-Reads! Alles aus der Drive API.
 * 
 * @param {string|null} categoryFilter - Kategorie-ID oder null für alle
 * @param {number|null} limitCount - Max Anzahl (null = alle)
 * @returns {Promise<Array>} Dokument-Objekte
 */
export async function loadDocuments(categoryFilter = null, limitCount = null) {
  if (!isDriveAvailable()) {
    console.warn('📄 Drive nicht verfügbar — keine Dokumente');
    return [];
  }

  let docs = await listDriveDocuments();

  // Kategorie-Filter
  if (categoryFilter) {
    docs = docs.filter(d => d.categoryId === categoryFilter);
  }

  // Sortierung: neueste zuerst
  docs.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));

  // Limit
  if (limitCount && docs.length > limitCount) {
    docs = docs.slice(0, limitCount);
  }

  return docs;
}

/**
 * Lädt Dokumente aus Google Drive seitenweise.
 * Ruft `onPage` für jede Seite mit den (gefilterten) Dokumenten dieser Seite
 * auf — die UI kann so die erste Seite sofort zeigen und den Rest im
 * Hintergrund nachladen.
 *
 * @param {string|null} categoryFilter - Kategorie-ID oder null
 * @param {(pageDocs: Array, info: {done: boolean, total: number}) => void} onPage
 * @param {number} [pageSize] - Drive-Seitengröße
 * @returns {Promise<Array>} ALLE (gefilterten) Dokument-Objekte
 */
export async function loadDocumentsPaged(categoryFilter = null, onPage = null, pageSize = 200) {
  if (!isDriveAvailable()) {
    console.warn('📄 Drive nicht verfügbar — keine Dokumente');
    if (onPage) onPage([], { done: true, total: 0 });
    return [];
  }

  const applyFilter = (docs) => categoryFilter
    ? docs.filter(d => d.categoryId === categoryFilter)
    : docs;

  const all = await listDriveDocumentsPaged((pageDocs, info) => {
    if (onPage) onPage(applyFilter(pageDocs), info);
  }, pageSize);

  return applyFilter(all);
}

/**
 * Lädt ein einzelnes Dokument (sucht in der gecachten Liste).
 */
export async function loadDocument(fileId) {
  const docs = await listDriveDocuments();
  return docs.find(d => d.id === fileId) || null;
}

/**
 * Speichert ein neues Dokument — Upload direkt nach Google Drive.
 * Metadaten im Dateinamen + appProperties. Kein Firestore!
 */
export async function uploadDocument(file, metadata, onProgress) {
  console.log('📄 Upload gestartet:', file?.name, file?.type, file?.size);

  if (onProgress) onProgress({ step: 'prepare', progress: 5 });

  // 1. Bild komprimieren
  let uploadFile = file;
  if (file.type?.startsWith('image/')) {
    try {
      uploadFile = await compressImage(file, 1600, 0.85);
      console.log('📄 Komprimiert:', Math.round(uploadFile.size / 1024), 'KB');
    } catch (e) {
      console.warn('📄 Komprimierung fehlgeschlagen, nutze Original:', e);
    }
  }
  if (onProgress) onProgress({ step: 'compress', progress: 15 });

  // 2. Dateiname generieren
  const ext = getExtension(file);
  const existingNames = metadata.existingNames || [];
  const fileName = generateDocumentName(
    metadata.categoryName || metadata.categoryId,
    metadata.supplier,
    metadata.date,
    ext,
    existingNames
  );

  // 3. appProperties für strukturierte Metadaten
  // Google Drive appProperties: max 124 Bytes pro Wert!
  const truncateProp = (val, maxBytes = 120) => {
    if (!val) return '';
    const str = String(val);
    // UTF-8 kann mehrbyte sein, sicher abschneiden
    const encoder = new TextEncoder();
    if (encoder.encode(str).length <= maxBytes) return str;
    let truncated = str;
    while (encoder.encode(truncated).length > maxBytes - 3) {
      truncated = truncated.slice(0, -10);
    }
    return truncated + '...';
  };

  const appProperties = {
    category: truncateProp(metadata.categoryName || metadata.categoryId || ''),
    categoryId: truncateProp(metadata.categoryId || ''),
    supplier: truncateProp(metadata.supplier || ''),
    date: metadata.date || new Date().toISOString().split('T')[0],
    notes: truncateProp(metadata.notes || ''),
    tags: truncateProp(metadata.tags ? JSON.stringify(metadata.tags) : '[]'),
    favorite: metadata.favorite ? 'true' : 'false',
    expiryDate: metadata.expiryDate || '',
    ocrText: truncateProp(metadata.ocrText || ''),
  };

  if (onProgress) onProgress({ step: 'upload', progress: 30 });

  // 4. Upload zu Google Drive
  const result = await uploadDocumentToDrive(
    uploadFile,
    fileName,
    file.type || 'image/jpeg',
    appProperties
  );

  if (onProgress) onProgress({ step: 'done', progress: 100 });

  console.log('📄 ✅ Upload komplett:', result.fileId, fileName);

  // 5. Dokument-Objekt zurückgeben (gleiche Struktur wie loadDocuments)
  const token = isDriveAvailable() ? undefined : null;
  return {
    id: result.fileId,
    name: fileName,
    title: fileName.replace(/\.\w+$/, '').replace(/_/g, ' '),
    fileName,
    mimeType: file.type || 'image/jpeg',
    fileSize: uploadFile.size || file.size || 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    categoryId: metadata.categoryId || '',
    categoryName: metadata.categoryName || '',
    supplier: metadata.supplier || '',
    date: metadata.date || new Date().toISOString().split('T')[0],
    notes: metadata.notes || '',
    tags: metadata.tags || [],
    favorite: metadata.favorite || false,
    expiryDate: metadata.expiryDate || '',
    ocrText: metadata.ocrText || '',
    fileUrl: getDriveFileUrlSync(result.fileId),
    thumbnailUrl: getDriveFileUrlSync(result.fileId),
    driveFileId: result.fileId,
    storageBackend: 'gdrive',
  };
}

/**
 * Aktualisiert Metadaten eines Dokuments.
 * Ändert Dateinamen + appProperties in Google Drive.
 */
export async function updateDocument(fileId, updates) {
  const driveUpdates = {};

  // appProperties aktualisieren
  // Google Drive appProperties: max 124 Bytes pro Wert!
  const truncateProp = (val, maxBytes = 120) => {
    if (!val) return '';
    const str = String(val);
    const encoder = new TextEncoder();
    if (encoder.encode(str).length <= maxBytes) return str;
    let truncated = str;
    while (encoder.encode(truncated).length > maxBytes - 3) {
      truncated = truncated.slice(0, -10);
    }
    return truncated + '...';
  };

  const propsUpdates = {};
  if (updates.supplier !== undefined) propsUpdates.supplier = truncateProp(updates.supplier);
  if (updates.notes !== undefined) propsUpdates.notes = truncateProp(updates.notes);
  if (updates.ocrText !== undefined) propsUpdates.ocrText = truncateProp(updates.ocrText);
  if (updates.categoryName) propsUpdates.category = truncateProp(updates.categoryName);
  if (updates.categoryId) propsUpdates.categoryId = truncateProp(updates.categoryId);
  if (updates.date) propsUpdates.date = updates.date;
  if (updates.tags !== undefined) propsUpdates.tags = truncateProp(JSON.stringify(updates.tags));
  if (updates.favorite !== undefined) propsUpdates.favorite = updates.favorite ? 'true' : 'false';
  if (updates.expiryDate !== undefined) propsUpdates.expiryDate = updates.expiryDate;

  if (Object.keys(propsUpdates).length > 0) {
    driveUpdates.appProperties = propsUpdates;
  }

  // Dateinamen neu generieren wenn Kategorie/Lieferant/Datum geändert
  if (updates.categoryName || updates.supplier !== undefined || updates.date) {
    const existing = await loadDocument(fileId);
    if (existing) {
      const ext = existing.fileName?.split('.').pop() || 'jpg';
      const newFileName = generateDocumentName(
        updates.categoryName || existing.categoryName,
        updates.supplier !== undefined ? updates.supplier : existing.supplier,
        updates.date || existing.date,
        ext
      );
      driveUpdates.name = newFileName;
    }
  }

  if (Object.keys(driveUpdates).length > 0) {
    await updateDriveDocument(fileId, driveUpdates);
  }
}

/**
 * Löscht ein Dokument aus Google Drive.
 */
export async function deleteDocument(fileId) {
  await deleteFromDrive(fileId);
  console.log('🗑️ Dokument gelöscht:', fileId);
}

// ============================================================
// 📊 STATISTIKEN (aus Drive-Daten berechnet)
// ============================================================

export async function getUniqueSuppliers() {
  const docs = await loadDocuments();
  return [...new Set(docs.map(d => d.supplier).filter(Boolean))].sort();
}

export async function getDocumentCounts() {
  const docs = await loadDocuments();
  const counts = {};
  docs.forEach(d => { counts[d.categoryId] = (counts[d.categoryId] || 0) + 1; });
  counts._total = docs.length;
  return counts;
}

// ============================================================
// 🔍 DUPLIKAT-ERKENNUNG
// ============================================================

/**
 * Berechnet MD5-Hash eines Files im Browser.
 * Vergleicht dann mit bestehenden Drive-Dokumenten.
 * @returns {object|null} Das existierende Dokument falls Duplikat, sonst null
 */
export async function checkDuplicate(file, existingDocs) {
  if (!file || !existingDocs?.length) return null;
  
  // Methode 1: Dateigröße + Name Matching (schnell)
  const sameSize = existingDocs.filter(d => d.fileSize && Math.abs(d.fileSize - file.size) < 100);
  if (sameSize.length === 0) return null;
  
  // Methode 2: MD5 per Crypto API berechnen
  try {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    
    // Drive liefert MD5, wir haben SHA-256 — Größen-Match als Fallback
    // Exakter Größen-Match + gleiche MIME-Type ist ein starker Duplikat-Indikator
    const duplicate = sameSize.find(d => 
      d.fileSize === file.size && 
      d.mimeType === file.type
    );
    
    return duplicate || null;
  } catch (e) {
    console.warn('📄 Duplikat-Check fehlgeschlagen:', e);
    return null;
  }
}

// ============================================================
// 🌐 URL-HILFSFUNKTIONEN
// ============================================================

/**
 * Löst eine Dokument-URL auf.
 * Drive-Dateien: Generiert authentifizierte URL.
 * Alte Firebase URLs: Direkt durchreichen.
 */
export function resolveDocumentUrlSync(url) {
  if (!url) return '';
  
  // Neue Drive-Dokumente: URL enthält bereits access_token
  if (url.includes('googleapis.com/drive/')) {
    return url;
  }
  
  // Legacy gdrive:// URLs
  if (url.startsWith('gdrive://')) {
    const fileId = url.replace('gdrive://', '');
    return getDriveFileUrlSync(fileId);
  }
  
  // Firebase Storage URLs
  return url;
}

export async function resolveDocumentUrl(url) {
  if (!url) return '';
  if (url.includes('googleapis.com/drive/')) return url;
  if (url.startsWith('gdrive://')) {
    const fileId = url.replace('gdrive://', '');
    return getDriveFileUrl(fileId);
  }
  return url;
}

/**
 * Lädt den Inhalt einer Dokument-Datei als Blob herunter.
 */
export async function downloadDocumentFile(docData) {
  if (docData.driveFileId) {
    return downloadDriveFile(docData.driveFileId);
  }
  // Legacy: Fetch über URL
  const response = await fetch(docData.fileUrl);
  if (!response.ok) throw new Error('Download fehlgeschlagen');
  return response.blob();
}
